// ForumWorkbench — per-agent wrapper over <CompositionWorkbench> for
// FORUM, the governance-paper composer. Surfaces FORUM's RAG status matrix
// and decisions-pending list via the `renderSections` slot.

import CompositionWorkbench from '.';
import { compositionAgentConfig } from './composition-agent-config';
import { cn } from '@/lib/utils';
import type { BaseDraft } from '@/data/types';

export interface ForumDraft extends BaseDraft {
    rag_status?: Array<{ area: string; rag: 'Green' | 'Amber' | 'Red' }>;
    decisions_pending?: string[];
}

interface ForumWorkbenchProps {
    draft: ForumDraft;
    readOnly?: boolean;
    onApproved?: (draft: ForumDraft) => void;
    onRejected?: (draft: ForumDraft) => void;
    onRevisionRequested?: (draft: ForumDraft) => void;
}

const RAG_CLS: Record<string, string> = {
    Green: 'bg-[rgb(var(--color-rag-green)/0.15)] text-[rgb(var(--color-rag-green))]',
    Amber: 'bg-[rgb(var(--color-rag-amber)/0.15)] text-[rgb(var(--color-rag-amber))]',
    Red: 'bg-[rgb(var(--color-rag-red)/0.15)] text-[rgb(var(--color-rag-red))]',
};

export default function ForumWorkbench({
    draft,
    readOnly,
    onApproved,
    onRejected,
    onRevisionRequested,
}: ForumWorkbenchProps) {
    return (
        <CompositionWorkbench<ForumDraft>
            agentName="FORUM"
            draft={draft}
            table={compositionAgentConfig.FORUM.table}
            actions={compositionAgentConfig.FORUM.actions}
            readOnly={readOnly}
            renderSections={(d) => (
                <div className="flex flex-col gap-2">
                    {d.rag_status && d.rag_status.length > 0 && (
                        <div
                            data-testid="forum-rag-matrix"
                            className="rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)] p-3"
                        >
                            <div className="text-card-title text-[rgb(var(--color-text-primary))] mb-2">
                                RAG status
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {d.rag_status.map((r, i) => (
                                    <span
                                        key={i}
                                        className={cn(
                                            'text-annotation px-2 py-0.5 rounded-button',
                                            RAG_CLS[r.rag],
                                        )}
                                    >
                                        {r.area}: {r.rag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {d.decisions_pending && d.decisions_pending.length > 0 && (
                        <div className="rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)] p-3">
                            <div className="text-card-title text-[rgb(var(--color-text-primary))] mb-2">
                                Decisions pending
                            </div>
                            <ul className="list-disc ml-5 flex flex-col gap-0.5">
                                {d.decisions_pending.map((dec, i) => (
                                    <li
                                        key={i}
                                        className="text-annotation text-[rgb(var(--color-text-secondary))]"
                                    >
                                        {dec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            onApproved={onApproved}
            onRejected={onRejected}
            onRevisionRequested={onRevisionRequested}
        />
    );
}
